import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';

/* Components */
import DefaultPermissions from '../../../sharedComponents/DefaultPermissions';

import {
  StylesPermissionsStepWraper,
  Content,
  ContentTitle,
  Label,
  CharCount,
  TextInput,
  InputGroup,
  ContentCheckbox,
  InputGroupCheckbox,
  OptionPermissionsTabs,
  OptionPermissionsTabsItem,
  ContentCustomPermissions,
  PermissionList,
} from '../styles';
import RenderIf from 'components/RenderIf';
import { Checkbox } from 'lego-dist';
import usePermission from 'hooks/usePermission';

export interface PermissionsStepProps {
  nextStep?: any;
  renderFooter?: any;
  formData?: any;
}

const PermissionsStep: React.FC<PermissionsStepProps> = ({
  renderFooter,
  formData,
}) => {
  const { t, i18n } = useTranslation();
  const [customPermissions, setCustomPermissions] = useState<boolean>(false);
  const { permissions = {} } = formData;
  const initialValues = { ...{ role_alias: t('ROLE_TYPES.DIRECTOR') }, ...permissions || {} };
  const { permissionsList, fetchPermissions } = usePermission();
  const [selectedSpecialPermissions, setSelectedSpecialPermissions] = useState<number[]>([]);
  const permissionsForm = Yup.object().shape({
    role_alias: Yup
      .string()
      .required(t('validations:FIELD_REQUIRED'))
      .trim()
      .max(70, t('validations:MAX_LENGTH', { amount: 70 }))
      .nullable(),
    permission_ids: Yup.array(),
  });

  useEffect(() => {
    fetchPermissions({
      scope: 'director',
      locale: i18n.language,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnClickToggle = useCallback((permission, checked) => {
    const isAlreadySelected = selectedSpecialPermissions.indexOf(permission.id) > -1;
    let selectedItems = [];

    if (isAlreadySelected) {
      selectedItems = selectedSpecialPermissions.filter(permissionId => permissionId !== permission.id);
    } else {
      selectedItems = [
        ...selectedSpecialPermissions,
        permission.id,
      ];
    }

    setSelectedSpecialPermissions(selectedItems);
  }, [selectedSpecialPermissions]);

  return (
    <StylesPermissionsStepWraper>
      <Formik
        initialValues={initialValues}
        validationSchema={permissionsForm}
        onSubmit={(res: any) => console.info('PermissionsStep Formik onSubmit', res)}
        validateOnBlur
        isInitialValid
        validateOnChange
        render={({ setFieldValue, errors, values, isValid }) => (
          <Form>
            <Content>
              <ContentTitle>{t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.TITLE')}</ContentTitle>
              <InputGroup>
                <Label>
                  <span>{t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.LABEL_RENAME_POSITION')}</span>
                  <CharCount>{`${values?.role_alias?.length} / 70`}</CharCount>
                </Label>
                <TextInput
                  onChange={(event: any) => {
                    setFieldValue('role_alias', event);
                  }}
                  placeholder={t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.ADD_RENAME_POSITION')}
                  value={values.role_alias}
                  errorMessage={errors.role_alias}
                />
              </InputGroup>

              <ContentCustomPermissions custom={customPermissions}>
                <OptionPermissionsTabs>
                  <OptionPermissionsTabsItem
                    onClick={() => setCustomPermissions(false)}
                    active={!customPermissions}
                  >
                    {t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.DEFAULT.TITLE')}
                  </OptionPermissionsTabsItem>
                  <OptionPermissionsTabsItem
                    onClick={() => setCustomPermissions(true)}
                    active={customPermissions}
                  >
                    {t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.SPECIAL.TITLE')}
                  </OptionPermissionsTabsItem>
                </OptionPermissionsTabs>
                <PermissionList>
                  { customPermissions ? (
                    permissionsList.map((permissionListItem: any, index: number) => (
                      <InputGroup key={permissionListItem.id}>
                        <Label>
                          {t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.PERMISSION')}
                          {' '}
                          {index + 1}
                        </Label>
                        <InputGroupCheckbox>
                          <Label>{permissionListItem.description}</Label>
                          <ContentCheckbox>
                            <Checkbox
                              label=" "
                              name="editPlansCheckbox"
                              onChange={(selected: boolean) => {
                                handleOnClickToggle(permissionListItem, selected);
                                setFieldValue('permission_ids', selectedSpecialPermissions);
                              }}
                            />
                          </ContentCheckbox>
                        </InputGroupCheckbox>
                      </InputGroup>
                    ))
                  ) : (
                    <DefaultPermissions role='Director' />
                  )}
                </PermissionList>
              </ContentCustomPermissions>
            </Content>
            <RenderIf condition={renderFooter}>
              {renderFooter(isValid, { permissions: values })}
            </RenderIf>
          </Form>
        )}
      />
    </StylesPermissionsStepWraper>
  );
};

export default PermissionsStep;
